import { vectorizeAsync } from '@c2s-npm/vectorize/vectorize.js';
import * as Ajax from "#base/util/ajax.js";

export const loadImage = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.src = src;
  });
};

export const uploadImageAsync = async (uploadUrl, file, data) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (data) {
    formData.append('data', JSON.stringify(data));
  }

  return Ajax.ajax({
    url: uploadUrl,
    method: 'POST',
    data: formData,
    contentType: false,
    processData: false,
  });
};

export const convertImageToPNGAsync = (src, destWidth, destHeight) => {
  return loadImage(src)
    .then(async (img) => {
      const canvas = document.createElement('canvas');
      canvas.width = destWidth;
      canvas.height = destHeight;

      const context = canvas.getContext('2d');
      context.imageSmoothingQuality = 'high';
      context.imageSmoothingEnabled = true;
      context.drawImage(img, 0, 0, destWidth, destHeight);

      const url = canvas.toDataURL('image/png');
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1))
      return { url, blob };
    });
};

export const loadAsText = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e?.target?.result);
    }
    reader.readAsText(file);
  });
};

export const convertSVGToPNG = (file, config) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async ({ target: { result: dataUrl } }) => {
      const dpi = 600 * 1.5;  // the actual design could have lots of margin, requiring expanding the image, so convert to a higher DPI than our target (600), just in case
      const svgImg = await loadImage(dataUrl);
      const aspect = svgImg.width / svgImg.height;
      let width, height;
      if (aspect >= 1) {
        width = config.print.width;
        height = config.print.width / aspect;
      } else {
        width = config.print.height * aspect;
        height = config.print.height;
      }
      width *= dpi / 25.4; // config.print units are millimeters
      height *= dpi / 25.4; // config.print units are millimeters
      width = Math.floor(width);
      height = Math.floor(height);
      
      const png = await convertImageToPNGAsync(dataUrl, width, height);
  
      resolve(png);
    };
    reader.readAsDataURL(file);
  });
}

export const combineImages = async (
  images,
  { maxHeight = 1000, maxWidth = 1000, orientation = 'vertical' } = {}
) => {
  const isVertical = orientation === 'vertical';

  const loaded = await Promise.all(images.map(loadImage));

  const width = isVertical
    ? Math.max(...loaded.map((l) => l.naturalWidth))
    : loaded.reduce((a, l) => (a += l.naturalWidth), 0);
  const height = isVertical
    ? loaded.reduce((a, l) => (a += l.naturalHeight), 0)
    : Math.max(...loaded.map((l) => l.naturalHeight));

  const ratio = Math.min(Math.min(maxWidth, width) / width, Math.min(maxHeight, height) / height);

  const canvas = document.createElement('canvas');
  canvas.width = width * ratio;
  canvas.height = height * ratio;

  const context = canvas.getContext('2d');
  context.imageSmoothingQuality = 'high';
  context.imageSmoothingEnabled = true;

  loaded.forEach((img, i) => {
    context.drawImage(
      img,
      isVertical ? 0 : loaded.slice(0, i).reduce((a, l) => (a += l.naturalWidth), 0) * ratio,
      isVertical ? loaded.slice(0, i).reduce((a, l) => (a += l.naturalHeight), 0) * ratio : 0,
      img.naturalWidth * ratio,
      img.naturalHeight * ratio
    );
  });

  const url = canvas.toDataURL();

  return url;
};

export const vectorizeImage = async (url, method = 'Auto') => {
  return await fetch(url)
    .then((r) => r.arrayBuffer())
    .then(async (data) => {
      const options = await vectorizeAsync(data, 'source');
      const option = options.find((o) => o.name === method) || options[0];
      const svgBase64 = btoa(option.svg.reduce((data, byte) => data + String.fromCharCode(byte), ''));
      const imageURI = 'data:image/svg+xml;base64,' + svgBase64;
      return imageURI;
    });
};

export const tryVectorizeImage = async (url, method = 'Auto') => {
  try {
    return await vectorizeImage(url, method);
  } catch (e) {
    console.warn(e);
    return null;
  }
};
